/* ==========================================================================
   Rook UI Core — Collapsible Controller
   Web Component: <rk-collapsible-element>
   ========================================================================== */

if (!customElements.get('rk-collapsible-element')) {
    class RkCollapsibleElement extends HTMLElement {
        constructor() {
            super();
            this.trigger = null;
            this.content = null;
            this.isOpen = false;

            this._onClick = this._onClick.bind(this);
            this._handleExternalToggle = this._handleExternalToggle.bind(this);
        }

        connectedCallback() {
            this.trigger = this.querySelector('.rk-collapsible__trigger');
            this.content = this.querySelector('.rk-collapsible__content');

            if (!this.content) return;

            // State init
            const defaultOpen = this.hasAttribute('data-open') && this.getAttribute('data-open') !== 'false';
            this.isOpen = defaultOpen;

            this._syncState(false); // sync without animation initially

            // Event binding
            if (this.trigger) {
                this.trigger.addEventListener('click', this._onClick);
            }

            // Expose public API events listeners for external triggers
            this.addEventListener('rk:collapsible:open', () => this.open());
            this.addEventListener('rk:collapsible:close', () => this.close());
            this.addEventListener('rk:collapsible:toggle', () => this.toggle());

            const id = this.getAttribute('id');
            if (id) {
                document.addEventListener('click', this._handleExternalToggle);
            }
        }

        disconnectedCallback() {
            if (this.trigger) {
                this.trigger.removeEventListener('click', this._onClick);
            }
            document.removeEventListener('click', this._handleExternalToggle);
        }

        /* ------------------------------------------------------------------ */
        /*  Public API                                                        */
        /* ------------------------------------------------------------------ */

        open() {
            if (this.isOpen) return;
            this.isOpen = true;
            this._syncState(true);
        }

        close() {
            if (!this.isOpen) return;
            this.isOpen = false;
            this._syncState(true);
        }

        toggle() {
            if (this.isOpen) {
                this.close();
            } else {
                this.open();
            }
        }

        _onClick(e) {
            e.preventDefault();
            if (this.trigger && this.trigger.disabled) return;
            this.toggle();
        }

        _handleExternalToggle(e) {
            const externalBtn = e.target.closest(`[data-collapsible-toggle="${this.id}"]`);
            if (externalBtn) {
                e.preventDefault();
                this.toggle();
            }
        }

        /* ------------------------------------------------------------------ */
        /*  DOM Sync                                                          */
        /* ------------------------------------------------------------------ */

        _syncState(animate = true) {
            // Manage classes for animation (CSS Grid 0fr -> 1fr approach)
            if (this.isOpen) {
                this.classList.add('rk-collapsible--open');
                this.content.removeAttribute('hidden');

                // Allow CSS transition to kick in if animating
                requestAnimationFrame(() => {
                    this.content.classList.add('rk-collapsible__content--expanded');
                });
            } else {
                this.classList.remove('rk-collapsible--open');
                this.content.classList.remove('rk-collapsible__content--expanded');

                // If not animating, hide immediately
                if (!animate) {
                    this.content.setAttribute('hidden', 'true');
                } else {
                    // Wait for CSS transition (typically 300ms) before hiding from screen readers
                    // We use an explicit listener or timeout. Timeout is safer if transition gets cancelled.
                    clearTimeout(this._hideTimeout);
                    this._hideTimeout = setTimeout(() => {
                        if (!this.isOpen) {
                            this.content.setAttribute('hidden', 'true');
                        }
                    }, 300); // Must match CSS duration
                }
            }

            // A11y
            if (this.trigger) {
                this.trigger.setAttribute('aria-expanded', this.isOpen.toString());
            }

            // Focus management inside content
            const focusables = this.content.querySelectorAll('a, button, input, select, textarea, [tabindex]');
            focusables.forEach(el => {
                if (this.isOpen) {
                    el.removeAttribute('tabindex');
                } else {
                    // Force unfocusable
                    el.setAttribute('tabindex', '-1');
                }
            });

            // Dispatch event
            this.dispatchEvent(
                new CustomEvent('rk:collapsible:change', {
                    bubbles: true,
                    detail: { isOpen: this.isOpen },
                })
            );
        }
    }

    customElements.define('rk-collapsible-element', RkCollapsibleElement);
}
